import React, { useEffect, useMemo, useState } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import { useTranslation } from 'react-i18next';
import DatePicker from '../../../components/layout/DatePicker';
import { PRESENTATION_STATE, PRESENTATION_TYPE } from '../consts';
import ClearIcon from '@mui/icons-material/Clear';
import { useGetEmpresasQuery } from '../../../services/companyApi';
import { useAppDispatch, useAppSelector } from '../../../hooks/reduxHooks';
import dayjs from 'dayjs';
import useContracts from '../../../components/layout/ContractSelector/hook/useContracts';
import { useRoles } from '../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../contexts/OrganizationFilterContext';
import useOrganizationSelector from '../../../components/layout/OrganizationSelector/hook/useOrganizationSelector';
import { setSelectedOrganizationId } from '../../../redux/slices/organizationsSlice';
import { useGetOrganismsQuery } from '../../../services/organismsApi';
import { PRESENTATION_TABS_INDEX } from '../../../redux/slices/presentationsSliceBO';
import { getYesNoOptions } from '../../Periodicos/consts/periodicosTypes';
import { ORIGEN_CARGA_ROL } from '../../../constants/origenCargaRol';

interface FiltersProps {
    extraFilters: any;
    handleChangeExtraFilters: (e: any) => void;
    isShowMoreFiltersOpen: boolean;
    currentTabIndex: number;
}

const FiltersComponent: React.FC<FiltersProps> = ({
    extraFilters,
    handleChangeExtraFilters,
    isShowMoreFiltersOpen,
    currentTabIndex
}) => {
    const { t } = useTranslation('presentaciones');
    const { isPerfilBackOffice } = useRoles();
    const { selectedOrganization, setSelectedOrganization } = useOrganizationFilter();
    const {
        data: { data: { data: organisms } } = { data: { data: [] } },
        isFetching: isFetchingOrganisms
    } = useGetOrganismsQuery({
        idOrganizacion: extraFilters?.idOrganizacion || undefined,
        skip: 0,
        take: 99999
    });

    const {
        data: establishmentsData,
        refetch,
        isFetching: isFetchingEstablishments
    } = useGetEmpresasQuery({
        skip: 0,
        take: 5000,
        idOrganismoCentral: extraFilters?.idOrganismoCentral
    });

    const { organizations: organizationsOptions, isFetching: isFetchingOrganizationsOptions } =
        useOrganizationSelector();
    const EXCLUDED_CUITS = ['30500052089', '30707519092'];

    const { contracts, isFetching: isFetchingContracts } = useContracts();
    const [allContracts, setAllContracts] = useState<any[]>([]);
    const [allOrganisms, setAllOrganisms] = useState<any[]>([]);
    const [allEstablishments, setAllEstablishments] = useState<any[]>([]);
    const [filteredOrganisms, setFilteredOrganisms] = useState<any[]>([]);
    const [filteredEstablishments, setFilteredEstablishments] = useState<any[]>([]);

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (contracts) {
            const formattedContracts = contracts.map((contract: any) => ({
                id: contract.id,
                nombre: `${contract.nombre}`
            }));
            setAllContracts(formattedContracts);
        }
    }, [contracts]);

    useEffect(() => {
        if (organisms && organisms?.length > 0) {
            const formattedOrganisms = organisms
                .filter((organismo: any) => !EXCLUDED_CUITS.includes(organismo.cuit))
                .map((organismo: any) => ({
                    label: `${organismo.cuit} - ${organismo.nombre}`,
                    value: organismo.id,
                    contrato: organismo.nroContrato,
                    idOrganismoCentral: organismo?.idOrganismoCentral
                }));
            setAllOrganisms(formattedOrganisms);
        }
    }, [organisms]);

    useEffect(() => {
        if (establishmentsData?.data?.data) {
            const formattedEstablishments = establishmentsData.data.data.map(
                (establishment: any) => ({
                    label: `${establishment.nombre} (${t('filters.codSRT')}: ${
                        establishment.codigoSRT
                    })`,
                    value: establishment.id,
                    organismoId: establishment.organismo?.id
                })
            );
            setAllEstablishments(formattedEstablishments);
        }
    }, [establishmentsData, t]);

    useEffect(() => {
        if (extraFilters?.idOrganismoCentral) {
            const filtered = allOrganisms.filter(
                (organismo) => organismo.idOrganismoCentral === extraFilters.idOrganismoCentral
            );
            setFilteredOrganisms(filtered);

            handleChangeExtraFilters({ target: { name: 'idOrganismo', value: null } });
        } else {
            setFilteredOrganisms(allOrganisms);
        }
    }, [extraFilters?.idOrganismoCentral, allOrganisms]);

    useEffect(() => {
        if (extraFilters?.idOrganismo) {
            const filtered = allEstablishments.filter(
                (establishment) => establishment.organismoId === extraFilters.idOrganismo
            );
            setFilteredEstablishments(filtered);
        } else {
            setFilteredEstablishments(allEstablishments);
        }
    }, [extraFilters?.idOrganismo, allEstablishments]);

    useEffect(() => {
        if (selectedOrganization && extraFilters?.idOrganizacion !== selectedOrganization) {
            handleChangeExtraFilters({
                target: {
                    name: 'idOrganizacion',
                    value: selectedOrganization
                }
            });
        }

        dispatch(setSelectedOrganizationId(selectedOrganization));
    }, [selectedOrganization, extraFilters?.idOrganizacion, handleChangeExtraFilters]);

    const handleOrganizationChange = (value: any) => {
        const organizationId = value?.id ?? null;

        setSelectedOrganization(organizationId);

        handleChangeExtraFilters({
            target: {
                name: 'idOrganizacion',
                value: organizationId
            }
        });
    };

    const presentationTypeOptions = Object.entries(PRESENTATION_TYPE)
        .filter(
            ([key, value]) => typeof value === 'number' && key !== 'RAR' && key !== 'CANCERIGENOS'
        )
        .map(([key, value]) => ({
            label: t(`types.${key.toLowerCase()}`),
            value
        }));

    const origenCargaRolOptions = [
        { label: t('origenCargaRolOptions.preventor'), value: ORIGEN_CARGA_ROL.PREVENTOR },
        {
            label: t('origenCargaRolOptions.operadorSupervisor'),
            value: ORIGEN_CARGA_ROL.OPERADOR_SUPERVISOR
        }
    ];

    const presentationStateOptions = Object.entries(PRESENTATION_STATE)
        .filter(
            ([key, value]) =>
                typeof value === 'number' &&
                !['BORRADOR', 'PROCESANDO', 'ERROR_PROCESAMIENTO', 'INACTIVO'].includes(key)
        )
        .map(([key, value]) => ({
            label: key === 'CANCELADO' ? 'RECHAZADO' : key.replace(/_/g, ' '),
            value
        }));

    const clearDate = (field: string) => {
        handleChangeExtraFilters({ target: { name: field, value: null } });
    };

    /**
     * La grilla SVCC no filtra por período y por eso acá no hay selector de año.
     * Los informes SVCC salen del RAR y su `fecha_presentacion` puede estar en
     * NULL: filtrar por el año derivado de esa fecha escondía filas válidas y
     * clasificaba mal el resto. El período se infiere por CUIT en el backend.
     */

    return (
        <Grid container spacing={2}>
            {isPerfilBackOffice && (
                <Grid item xs={12} sm={3} md={3}>
                    <AutocompleteComponent
                        value={
                            organizationsOptions.find((org) => org.id === selectedOrganization) ||
                            null
                        }
                        options={organizationsOptions || []}
                        size="small"
                        name="idOrganizacion"
                        loading={isFetchingOrganizationsOptions}
                        disabled={isFetchingOrganizationsOptions}
                        label={t('filters.organization')}
                        templateLabel={(option: any) => {
                            return `${option?.nombre}`;
                        }}
                        onChange={handleOrganizationChange}
                    />
                </Grid>
            )}

            <Grid item xs={12} sm={3} md={3}>
                <AutocompleteComponent
                    name="idOrganismoCentral"
                    label={t('filters.contract')}
                    value={
                        allContracts.find(
                            (option) => option.id === extraFilters?.idOrganismoCentral
                        ) || null
                    }
                    options={allContracts}
                    templateLabel={(option: any) => option?.nombre || ''}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: {
                                name: 'idOrganismoCentral',
                                value: value?.id ?? undefined
                            }
                        });
                    }}
                    loading={isFetchingContracts}
                    disabled={isFetchingContracts}
                />
            </Grid>

            {/*
              * Organismo y Establecimiento no aplican a la grilla SVCC: ahí la
              * unidad es el CUIT (organismo central), no el establecimiento.
              */}
            {currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC && (
            <Grid item xs={12} sm={3} md={3}>
                <AutocompleteComponent
                    name="idOrganismo"
                    label={t('filters.organism')}
                    value={
                        filteredOrganisms.find(
                            (option) => option.value === extraFilters?.idOrganismo
                        ) || null
                    }
                    options={filteredOrganisms}
                    templateLabel={(option: any) => option?.label || ''}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: { name: 'idOrganismo', value: value?.value || '' }
                        });
                    }}
                    loading={isFetchingOrganisms}
                    disabled={isFetchingOrganisms}
                />
            </Grid>
            )}

            {currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC && (
            <Grid item xs={12} sm={3} md={3}>
                <AutocompleteComponent
                    name="idEstablecimiento"
                    label={t('filters.establishment')}
                    value={
                        filteredEstablishments.find(
                            (option) => option.value === extraFilters?.idEstablecimiento
                        ) || null
                    }
                    options={filteredEstablishments}
                    templateLabel={(option: any) => option?.label || ''}
                    onChange={(value: any) => {
                        handleChangeExtraFilters({
                            target: { name: 'idEstablecimiento', value: value?.value || '' }
                        });
                    }}
                    loading={isFetchingEstablishments}
                    disabled={isFetchingEstablishments}
                />
            </Grid>
            )}

            {currentTabIndex === PRESENTATION_TABS_INDEX.RGRL && (
                <Grid item xs={12} sm={3} md={3}>
                    <AutocompleteComponent
                        name="origenCargaRol"
                        label={t('filters.origenCargaRol')}
                        value={
                            origenCargaRolOptions.find(
                                (option) => option.value === extraFilters?.origenCargaRol
                            ) || null
                        }
                        options={origenCargaRolOptions}
                        templateLabel={(option: any) => option?.label || ''}
                        onChange={(value: any) =>
                            handleChangeExtraFilters({
                                target: { name: 'origenCargaRol', value: value?.value }
                            })
                        }
                    />
                </Grid>
            )}

            {/*
              * En SVCC los "más filtros" no aplican: Estado lo cubren los badges
              * de la grilla y las fechas las cubre el Período (la DDJJ es anual).
              */}
            {isShowMoreFiltersOpen && currentTabIndex !== PRESENTATION_TABS_INDEX.SVCC && (
                <>
                    {currentTabIndex === PRESENTATION_TABS_INDEX.RGRL && (
                        <Grid item xs={12} sm={6} md={2}>
                            <AutocompleteComponent
                                name="idTipo"
                                label={t('filters.presentationType')}
                                value={
                                    presentationTypeOptions.find(
                                        (option) => option.value === extraFilters?.idTipo
                                    ) || null
                                }
                                options={presentationTypeOptions}
                                templateLabel={(option: any) => option?.label || ''}
                                onChange={(value: any) =>
                                    handleChangeExtraFilters({
                                        target: { name: 'idTipo', value: value?.value }
                                    })
                                }
                            />
                        </Grid>
                    )}
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={currentTabIndex !== PRESENTATION_TABS_INDEX.RGRL ? 3 : 2}
                    >
                        <AutocompleteComponent
                            name="estado"
                            label={t('filters.state')}
                            value={
                                presentationStateOptions.find(
                                    (option) => option.value === extraFilters?.estado
                                ) || null
                            }
                            options={presentationStateOptions}
                            templateLabel={(option: any) => option?.label || ''}
                            onChange={(value: any) =>
                                handleChangeExtraFilters({
                                    target: { name: 'estado', value: value?.value }
                                })
                            }
                        />
                    </Grid>

                    {currentTabIndex === PRESENTATION_TABS_INDEX.RGRL && (
                        <Grid item xs={12} sm={6} md={2}>
                            <AutocompleteComponent
                                name="tieneCancerigenos"
                                label={t('filters.isCarcinogenic')}
                                value={
                                    getYesNoOptions(t).find(
                                        (option) => option.value === extraFilters?.tieneCancerigenos
                                    ) || null
                                }
                                options={getYesNoOptions(t)}
                                onChange={(value: any) => {
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'tieneCancerigenos',
                                            value: value ? value.value : undefined,
                                            id: value?.id
                                        }
                                    });
                                }}
                            />
                        </Grid>
                    )}

                    {currentTabIndex === PRESENTATION_TABS_INDEX.RAR && (
                        <Grid item xs={12} sm={6} md={3}>
                            <AutocompleteComponent
                                name="tieneExpuestos"
                                label={t('filters.isExposed')}
                                value={
                                    getYesNoOptions(t).find(
                                        (option) => option.value === extraFilters?.tieneExpuestos
                                    ) || null
                                }
                                options={getYesNoOptions(t)}
                                onChange={(value: any) => {
                                    handleChangeExtraFilters({
                                        target: {
                                            name: 'tieneExpuestos',
                                            value: value ? value.value : undefined,
                                            id: value?.id
                                        }
                                    });
                                }}
                            />
                        </Grid>
                    )}

                    <Grid item xs={12} sm={6} md={2.9} style={{ position: 'relative' }}>
                        <DatePicker
                            name="fechaDesde"
                            label={t('filters.startDate')}
                            value={extraFilters?.fechaDesde || null}
                            onChange={(date: Date | null) => {
                                if (
                                    date &&
                                    extraFilters?.fechaHasta &&
                                    dayjs(date).isAfter(dayjs(extraFilters.fechaHasta))
                                ) {
                                    handleChangeExtraFilters({
                                        target: { name: 'fechaHasta', value: null }
                                    });
                                }
                                handleChangeExtraFilters({
                                    target: { name: 'fechaDesde', value: date }
                                });
                            }}
                            disableFuture={true}
                            format="DD/MM/YYYY"
                        />
                        {extraFilters?.fechaDesde && (
                            <IconButton
                                onClick={() => clearDate('fechaDesde')}
                                style={{ position: 'absolute', top: 28, right: 40 }}
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={0.2}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Typography
                            variant="h3"
                            sx={{ fontWeight: 'bold', mt: 2, color: '#707070' }}
                        >
                            -
                        </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2.9} style={{ position: 'relative' }}>
                        <DatePicker
                            name="fechaHasta"
                            label={t('filters.endDate')}
                            value={extraFilters?.fechaHasta || null}
                            onChange={(date: Date | null) => {
                                if (
                                    date &&
                                    extraFilters?.fechaDesde &&
                                    dayjs(date).isBefore(dayjs(extraFilters.fechaDesde))
                                ) {
                                    handleChangeExtraFilters({
                                        target: { name: 'fechaDesde', value: null }
                                    });
                                }
                                handleChangeExtraFilters({
                                    target: {
                                        name: 'fechaHasta',
                                        value: date ? dayjs(date).endOf('day').toISOString() : null
                                    }
                                });
                            }}
                            disableFuture={true}
                            minDate={extraFilters?.fechaDesde || null}
                            format="DD/MM/YYYY"
                        />
                        {extraFilters?.fechaHasta && (
                            <IconButton
                                onClick={() => clearDate('fechaHasta')}
                                style={{ position: 'absolute', top: 28, right: 40 }}
                            >
                                <ClearIcon />
                            </IconButton>
                        )}
                    </Grid>
                </>
            )}
        </Grid>
    );
};

export default FiltersComponent;
